/**
 * PublicObstructionRow
 *
 * Rich, read-only obstruction row for the public artifact list. Mirrors the
 * authenticated `ObstructionCard` (status dot + title + type/severity/impact
 * badges + road + planned date range) but is fully prop-driven so it works on
 * the unauthenticated share/embed pages.
 *
 * @module components/share/public/PublicArtifactList/parts/PublicObstructionRow
 */
import { memo, type CSSProperties } from 'react';
import { Badge } from '@mantine/core';
import { IconCalendar, IconRoad } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import {
  OBSTRUCTION_STATUS_COLORS,
  SEVERITY_COLORS,
} from '@/constants/obstructions';
import { ICON_SIZE_TAB } from '@/constants/ui';
import type { ObstructionStatus, SeverityLevel } from '@/types/obstructions';
import type { PublicObstructionListItem } from '@/types/share';

import { PublicArtifactRowCard } from './PublicArtifactRowCard';
import styles from './PublicArtifactRows.module.css';

interface PublicObstructionRowProps {
  obstruction: PublicObstructionListItem;
  onClick: () => void;
}

export const PublicObstructionRow = memo(function PublicObstructionRow({
  obstruction,
  onClick,
}: PublicObstructionRowProps) {
  const { t } = useTranslation();
  const { formatDateRange } = useLocalizedDate();

  const statusStyle = {
    '--dot-color':
      OBSTRUCTION_STATUS_COLORS[obstruction.status as ObstructionStatus],
  } as CSSProperties;

  const severityStyle = {
    '--obstruction-severity-color':
      SEVERITY_COLORS[obstruction.severity as SeverityLevel],
  } as CSSProperties;

  return (
    <PublicArtifactRowCard
      ariaLabel={`${t('obstructions.openDetails')}: ${obstruction.title}`}
      onClick={onClick}
    >
      <div className={styles.titleRow}>
        <span
          className={styles.statusDot}
          style={statusStyle}
          aria-hidden="true"
        />
        <span className={styles.title}>{obstruction.title}</span>
      </div>

      <div className={styles.badgesRow}>
        <Badge size="xs" variant="light" color="gray">
          {t(`obstructions.type.${obstruction.type}`)}
        </Badge>
        <Badge
          size="xs"
          variant="filled"
          className={styles.severityBadge}
          style={severityStyle}
        >
          {t(`obstructions.severity.${obstruction.severity}`)}
        </Badge>
        <Badge size="xs" variant="outline" color="gray">
          {t(`obstructions.impact.${obstruction.impact}`)}
        </Badge>
      </div>

      {obstruction.road !== null && obstruction.road.length > 0 && (
        <div className={styles.metaRow}>
          <div className={styles.metaGroup}>
            <IconRoad
              size={ICON_SIZE_TAB}
              className={styles.metaIcon}
              aria-label={t('obstructions.card.location')}
            />
            <span className={styles.metaText}>{obstruction.road}</span>
          </div>
        </div>
      )}

      <div className={styles.metaRow}>
        <div className={styles.metaGroup}>
          <IconCalendar size={ICON_SIZE_TAB} className={styles.metaIcon} />
          <span className={styles.metaText}>
            {formatDateRange(
              obstruction.plannedStartDate,
              obstruction.plannedEndDate
            )}
          </span>
        </div>
      </div>
    </PublicArtifactRowCard>
  );
});
